import { set, get, isEmpty, escape, shuffle, debounce } from "lodash-es";
import {
  getValueOrLabelByFieldId,
  getElementsGroupById,
} from "@/helpers/utils";

import LockIndicator from "@/components/form_fields/shared/lock-indicator.vue";

const sharedActions = {
  components: {
    LockIndicator,
  },
  props: {
    styling: {
      type: Object,
      required: true,
    },
    questionNumber: {
      type: Number,
      default: 0,
    },
    formElements: {
      type: Array,
      required: true,
    },
    variables: {
      type: Array,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    hasMultipleFields: {
      type: Boolean,
      default: false,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    allowedRoleIds: {
      type: Array,
      default: () => [],
    },
    allowAllFields: {
      type: Boolean,
      default: false,
    },
    formExecutableResponse: {
      type: Object,
      default: () => ({}),
    },
    sourceFieldId: {
      type: String,
      required: false, // Changed from true to false to match form-component.vue
      default: null,
    },
  },
  emits: [
    "next",
    "form-value-changed",
    "field-focus",
    "field-blur",
    "broadcast-field-update",
  ],
  data() {
    return {
      timeout: null,
      updatingDynamicOptions: false,
      childMap: {}, // Map to store child items by their IDs
    };
  },
  computed: {
    label() {
      const { isHidden, label, htmlLabel } = this.item;

      if (!this.showLabel) return "";

      let question = this.replaceTagWithAnswer(htmlLabel || label);

      const showQuestionNumber =
        this.questionNumber && this.styling.showQuestionNumber && !isHidden;

      return showQuestionNumber
        ? `Q${this.questionNumber}. ${question}`
        : question;
    },
    description() {
      if (!this.showLabel) return;

      return this.replaceTagWithAnswer(this.item.description);
    },
    itemValue: {
      get() {
        return this.getItemValue(this.item);
      },
      set(newValue) {
        this.setItemValue(this.item, newValue);
      },
    },
    correctOptions() {
      return this.item.options.filter(
        (option) =>
          this.item.quizScore.hasOwnProperty(option.id) &&
          this.item.quizScore[option.id].value > 0,
      );
    },
    correctAnswers() {
      return this.correctOptions.map((option) => option.id);
    },
    correctAnswerLabels() {
      return this.correctOptions.map((option) => option.label);
    },
    revealAnswer: {
      get() {
        return (
          this.item.isQuizField &&
          !this.editing &&
          this.hasPositiveScores &&
          this.styling.showFeedback &&
          this.item.revealAnswer
        );
      },
      set(value) {
        set(this.item, "revealAnswer", value);
      },
    },
    isEditingDisabled() {
      return this.revealAnswer && !this.item.allowEditing;
    },
    revealAnswerStyle() {
      return {
        color: this.styling.fontColor || "#575757",
        "user-select": "none",
        "margin-top": "8px",
      };
    },
    showAnswerText() {
      return (
        !this.editing &&
        this.item.isQuizField &&
        this.hasPositiveScores &&
        this.styling.showFeedback &&
        !isEmpty(this.item.value) &&
        !this.revealAnswer
      );
    },
    hasPositiveScores() {
      // Check if any options has score more than 0 (correct answer)
      return Object.values(this.item.quizScore).some(
        (values) => values.value > 0,
      );
    },
    isDisabled() {
      // Check for property called hasFieldAssignee in item
      if (this.item.hasOwnProperty("hasFieldAssignee")) {
        if (!this.item.hasFieldAssignee) return false;

        if (this.allowAllFields) return false;

        const fieldRoleId = parseInt(this.item.fieldRoleId);
        if (!fieldRoleId) return true;

        return !this.allowedRoleIds.includes(fieldRoleId);
      }
      return false;
    },
    isLocked() {
      return this.item.locked || false;
    },
    isFieldDisabled() {
      return this.fieldDisabled(this.item);
    },
    fieldStyles() {
      return this.lockedFieldStyles(this.item);
    },
    shuffledOptions() {
      if (!this.item.hasOwnProperty("options")) return [];
      if (
        this.editing ||
        (!this.styling.allowRandomizeOptions &&
          !this.item.allowRandomizeOptions)
      ) {
        return this.item.options;
      } else {
        return shuffle(this.item.options);
      }
    },
  },
  watch: {
    "item.value": {
      handler(newValue) {
        this.handleFieldUpdate(this.item, newValue);
      },
      deep: true,
    },
    "item.customOptionValue": {
      handler() {
        this.handleFieldUpdate(this.item, this.item.customOptionValue);
      },
    },
    "item.children": {
      handler() {
        if (!this.item.children) return;
        this.childMap = getElementsGroupById(this.item.children);
      },
      immediate: true,
    },
    // Watch for changes in form data that might affect dynamic values
    // formElements: {
    //   handler() {
    //     this.updateFieldValue(this.item);
    //   },
    //   deep: true,
    // },
    variables: {
      handler() {
        this.updateItemValue(this.item);
      },
      deep: true,
    },
    formExecutableResponse: {
      handler() {
        this.updateItemValue(this.item);
        this.updateDynamicOptions(this.item);
      },
      deep: true,
    },
  },
  mounted() {
    this.updateDynamicOptions(this.item);
  },
  created() {
    // Create a unique debounced function per component instance
    this.debouncedBroadcast = debounce(function (item, value) {
      this.$emit("broadcast-field-update", { item, value });
    }, 300);
  },
  methods: {
    handleFieldUpdate(item, value) {
      if (!this.item.componentFromRepeatField) {
        this.$emit("form-value-changed", this.item);
      }

      // Only broadcast if this is NOT a collaborative update
      if (!item._isCollaborativeUpdate && this.item.type !== "signature") {
        this.debouncedBroadcast(item, value);
      }
    },
    handleFocus(item) {
      if (!this.editing) {
        const id = item ? item.id : this.item.id;
        this.$emit("field-focus", id);
      }
    },
    handleBlur(item) {
      if (!this.editing) {
        const id = item ? item.id : this.item.id;
        this.$emit("field-blur", id);
      }
    },
    lockedFieldStyles(item) {
      if (item.locked && item.lockedBy) {
        return {
          border: `2px solid ${item.lockedBy.userColor}`,
          backgroundColor: `${item.lockedBy.userColor}15`,
          boxShadow: `0 0 0 2px ${item.lockedBy.userColor}40`,
          cursor: "not-allowed",
          opacity: "0.7",
          transition: "all 0.2s ease",
        };
      }
      return {};
    },
    fieldDisabled(item) {
      return item.locked || this.isDisabled;
    },
    autoProgressToNext() {
      if (
        this.styling.multiPage &&
        !this.hasMultipleFields &&
        this.styling.autoProgression &&
        !this.styling.showFeedback
      ) {
        if (this.timeout) {
          // clear previous timeout if not executed yet
          clearTimeout(this.timeout);
          this.timeout = null;
        }
        this.timeout = setTimeout(
          () => {
            this.$emit("next");
            this.timeout = null;
          },
          this.styling?.instantNextPage ? 100 : 750,
        );
      }
    },
    replaceTagWithAnswer(question, showOnlyValue = false) {
      const regex = /@\{(.*?)\}/;
      let matches = question?.match(regex);

      while (matches?.length > 0) {
        let replaceValue = this.editing
          ? this.createInformationRecallBadge(matches[1])
          : this.retrieveFieldData(matches[1]);

        if (showOnlyValue) {
          replaceValue = this.retrieveFieldData(matches[1]);
        }

        question = question.replace(matches[0], replaceValue);
        matches = question.match(regex);
      }

      return question;
    },
    createInformationRecallBadge(id) {
      const value = getValueOrLabelByFieldId({
        id,
        items: this.formElements,
        variables: this.variables,
      });
      return this.createStyledBadge(value);
    },
    createStyledBadge(value) {
      return `<span style="background: ${this.styling.buttonColor || "#00A3FF"};
          border-radius: 6px;
          color: ${this.styling.buttonTextColor || "white"};
          margin: 1px 0;
          padding: 2px 6px"
        >${value}</span>`;
    },
    retrieveFieldData(id) {
      const value = getValueOrLabelByFieldId({
        id,
        items: this.formElements,
        variables: this.variables,
        dynamicData: this.formExecutableResponse,
        outputType: "only-value",
      });
      return escape(value);
    },
    processInputValue(value) {
      const regex = /@{([^}]+)}/g;
      const regExToGetNumber = /\d+(\.\d+)?/;
      let processedValue = value.toString();
      let matches = processedValue.match(regex);

      while (matches?.length > 0) {
        const match = matches[0]; // Get the full match including '@{}'
        let matchWithoutSymbol = match.slice(2, -1); // Remove the '@{' and '}' symbols

        if (matchWithoutSymbol.startsWith("variable_")) {
          //get variable uuid if matchWithoutSymbol is variable
          matchWithoutSymbol = matchWithoutSymbol.split("_")[1];
        }

        let processedMatch = this.retrieveFieldData(matchWithoutSymbol); // Get the processed value for the match
        let processedMatchToNumber = 0;

        // conver the value to Array and add each value to get final value
        processedMatch.split(",").forEach((value) => {
          // get the number from the value ex: 10% => 10, Product 1 - $5.5 => 5.5, 1 => 1
          const matched = value.match(regExToGetNumber);
          const number = matched ? Number(matched[0]) : 0;
          processedMatchToNumber += number;
        });

        processedValue = processedValue.replace(match, processedMatchToNumber);
        matches = processedValue.match(regex);
      }
      return processedValue;
    },
    parsePlaceholders(text) {
      if (!text || typeof text !== "string") return text || "";

      const regex = /@\{(.*?)\}/g;
      let result = text;
      let matches = result.match(regex);

      while (matches?.length > 0) {
        const match = matches[0]; // Full match including @{}
        const id = match.slice(2, -1); // Remove @{ and }

        let replacement = "";
        if (this.editing) {
          // In editing mode, show styled badges
          const value = getValueOrLabelByFieldId({
            id,
            items: this.formElements,
            variables: this.variables,
            dynamicData: this.formExecutableResponse,
            outputType: "only-label",
          });
          replacement = this.createStyledBadge(value || id);
        } else {
          // In display mode, show actual values
          replacement = getValueOrLabelByFieldId({
            id,
            items: this.formElements,
            variables: this.variables,
            dynamicData: this.formExecutableResponse,
            outputType: "only-value",
          });
          replacement = escape(replacement || "");
        }

        result = result.replace(match, replacement);
        matches = result.match(regex);
      }

      return result;
    },
    updateFieldValue(item, parentItem = null) {
      if (
        item.defaultValue &&
        typeof item.defaultValue === "string" &&
        item.defaultValue.startsWith("@{")
      ) {
        // Extract the ID from @{id} format
        const match = item.defaultValue.match(/@\{(.*?)\}/);
        if (match && match[1]) {
          const templateId = match[1];
          // Only update if currentUpdatingField matches the referenced field

          const targetFieldChanged = parentItem
            ? parentItem.id === this.sourceFieldId && templateId === item.id
            : this.sourceFieldId === templateId;

          if (targetFieldChanged) {
            this.updateItemValue(item);
          }
        }
      }

      // Recursively update nested items
      if (item.children) {
        item.children.forEach((childItem) => {
          this.updateFieldValue(childItem, item);
        });
      }
    },
    updateItemValue(item) {
      if (
        item.defaultValue &&
        typeof item.defaultValue === "string" &&
        item.defaultValue.startsWith("@{")
      ) {
        const resolvedValue = this.parsePlaceholders(item.defaultValue);
        set(item, "value", resolvedValue);
      }

      // Recursively update nested items
      if (item.children) {
        item.children.forEach((childItem) => {
          this.updateItemValue(childItem);
        });
      }
    },
    getItemValue(item) {
      if (this.editing) return "";
      const currentValue = item.value;
      if (
        currentValue &&
        typeof currentValue === "string" &&
        currentValue.includes("@{")
      ) {
        const resolvedValue = this.parsePlaceholders(currentValue);
        set(item, "value", resolvedValue);
        return resolvedValue;
      }

      return currentValue;
    },
    setItemValue(item, newValue) {
      set(item, "value", newValue);
    },
    updateDynamicOptions(item) {
      // Prevent recursive updates
      if (this.updatingDynamicOptions || this.editing) {
        return;
      }

      // Only process if dynamic options are enabled and we have a variable
      if (!item.dynamicOptionsFromApi || !item.dynamicOptionsVariable) {
        return;
      }

      this.updatingDynamicOptions = true;

      try {
        // Extract the variable ID from @{id} format
        const regex = /@\{(.*?)\}/;
        const matches = item.dynamicOptionsVariable.match(regex);
        if (!matches || !matches[1]) {
          return;
        }

        const variableId = matches[1];

        // Get the actual value from the dynamic data
        const value = getValueOrLabelByFieldId({
          id: variableId,
          items: this.formElements,
          variables: this.variables,
          dynamicData: this.formExecutableResponse,
          outputType: "only-value",
        });

        // Only proceed if we have a valid array
        if (!Array.isArray(value) || value.length === 0) {
          return;
        }

        let dynamicOptions = [];

        // Check if it's an array of objects by testing the first element
        const isArrayOfObjects =
          typeof value[0] === "object" && value[0] !== null;

        if (isArrayOfObjects) {
          // Handle array of objects using mapVariableToLabel
          let labelField = item.mapVariableToLabel;
          if (!labelField) {
            this.updateDynamicOptions = false;
            return;
          }

          // remove the @{dynamicData[index].} prefix if it exists
          if (labelField.startsWith("@{dynamicData[")) {
            labelField = labelField.replace(
              /@{dynamicData\[\d+\]\.(.*)\}$/,
              "$1",
            );
          }

          if (labelField && get(value[0], labelField)) {
            dynamicOptions = value.map((obj) => ({
              id: get(obj, labelField),
              label: get(obj, labelField),
            }));
          }
        } else {
          // Handle primitive array
          dynamicOptions = value.map((primitiveValue) => ({
            id: primitiveValue.toString(),
            label: primitiveValue.toString(),
            value: primitiveValue.toString(),
          }));
        }

        // Update the item options directly
        if (dynamicOptions.length > 0) {
          set(item, "options", dynamicOptions);
        }
      } finally {
        this.updatingDynamicOptions = false;
      }
    },
  },
};

export default sharedActions;
